import { useEffect, useState } from "react";
import type { ControllerRenderProps } from "react-hook-form";
import { useFormContext, useWatch } from "react-hook-form";
import { useFormatCurrency } from "utils/customHooks/useFormatCurrency";

import {
  PercentOrMoneyInput,
  type PercentOrMoneyMode,
} from "@krea/shared/design-system/input/percent-or-money-input";
import type { Currency } from "@krea/shared/types/common";

import type { IBuildingCreditBidInput } from "@/features/lender-responses/types/lender-response";
import type { BidPostData } from "@/validations";

interface ISetupFeeInputProps {
  field: ControllerRenderProps<
    BidPostData<IBuildingCreditBidInput>,
    `bids.${number}.setupFee`
  >;
  bidFormIndex: number;
  currency: Currency;
}

// Backend only stores the setup fee as an amount, so a percentage is
// converted here from the credit limit and only the amount is ever sent.
export const SetupFeeInput = ({
  field,
  bidFormIndex,
  currency,
}: ISetupFeeInputProps) => {
  const { control } = useFormContext<BidPostData<IBuildingCreditBidInput>>();
  const creditLimit = useWatch({
    control,
    name: `bids.${bidFormIndex}.amount`,
  });
  const [formatCurrency] = useFormatCurrency({ currency });

  // A saved fee has no known percentage, so editing starts in amount mode.
  const [mode, setMode] = useState<PercentOrMoneyMode>(
    field.value != null ? "amount" : "percent",
  );
  const [percent, setPercent] = useState<number | null>(null);

  const { value, onChange } = field;
  useEffect(() => {
    if (mode !== "percent") return;
    const next =
      percent != null && creditLimit
        ? Math.round((creditLimit * percent) / 100)
        : null;
    if (next !== (value ?? null)) onChange(next);
  }, [mode, percent, creditLimit, value, onChange]);

  const handleModeChange = (next: PercentOrMoneyMode) => {
    setMode(next);
    // The amount carries over; a percentage cannot be recovered from it
    // without rounding drift, so going back to percent starts empty.
    if (next === "percent") {
      setPercent(null);
      onChange(null);
    }
  };

  return (
    <>
      <PercentOrMoneyInput
        ref={field.ref}
        id={field.name}
        aria-required
        currency={currency}
        mode={mode}
        onModeChange={handleModeChange}
        value={mode === "percent" ? percent : (value ?? null)}
        onChange={mode === "percent" ? setPercent : onChange}
        onBlur={field.onBlur}
        placeholder={mode === "percent" ? "e.g. 2" : undefined}
      />
      {mode === "percent" && (
        <p className="text-helper">
          {value != null
            ? `= ${formatCurrency(value)} of the credit limit`
            : "Calculated from the credit limit"}
        </p>
      )}
    </>
  );
};
